/**
 * Day-over-day change of a dam's release / C.13 discharge / % capacity
 * (SPEC v0.9.4a A2–A5). Pure logic shared by the map label, the dam card and
 * the result-card bar. Compares ONLY two real reports with DIFFERENT report
 * dates; no previous report → null (never compared against 0, A5).
 */
/** Thresholds (A3/A4) — the one place to tune them. */
export const DAM_CHANGE = {
    /** reservoir dam — significant change (amber bar on a rise, map ▲/▼ either way) when
     *  |change| ≥ damPct % AND ≥ damMinCms ลบ.ม./วิ … */
    damPct: 50,
    damMinCms: 50,
    /** … or |change| ≥ damAnyCms ลบ.ม./วิ on its own */
    damAnyCms: 100,
    /** C.13 — significant change (amber on a rise, map ▲/▼ either way) when |change| ≥ this */
    c13Cms: 100,
};
const dayNum = (iso) => Date.parse(iso + "T00:00:00Z") / 86400000;
/** Both reports real and on different dates? Returns the day gap, else null. */
function dayGap(cur, prev) {
    if (!cur || !prev || cur.value == null || prev.value == null)
        return null;
    if (!Number.isFinite(cur.value) || !Number.isFinite(prev.value))
        return null;
    const gap = dayNum(cur.valueDate) - dayNum(prev.valueDate);
    return Number.isFinite(gap) && gap >= 1 ? gap : null; // same report date = same round, not a change
}
/** Release change of a reservoir dam ("dam") or the C.13 barrage ("c13"). */
export function releaseChange(cur, prev, kind) {
    const gap = dayGap(cur, prev);
    if (gap == null)
        return null;
    const c = cur.value, p = prev.value;
    const delta = Math.round((c - p) * 10) / 10;
    const pct = p > 0 ? (delta / p) * 100 : delta > 0 ? Infinity : 0;
    const dir = delta > 0 ? "up" : delta < 0 ? "down" : "same";
    const abs = Math.abs(delta);
    const significant = dir !== "same" && (kind === "c13"
        ? abs >= DAM_CHANGE.c13Cms
        : (Math.abs(pct) >= DAM_CHANGE.damPct && abs >= DAM_CHANGE.damMinCms) || abs >= DAM_CHANGE.damAnyCms);
    const arrow = significant, highlight = significant && dir === "up";
    return { dir, prev: p, cur: c, delta, pct, prevDate: prev.valueDate, curDate: cur.valueDate, isYesterday: gap === 1, arrow, highlight };
}
/** % capacity vs the previous report (dam card "111% ▲ จาก 110%"). */
export function percentChange(cur, prev) {
    const gap = dayGap(cur, prev);
    if (gap == null)
        return null;
    const c = cur.value, p = prev.value;
    const d = Math.round((c - p) * 10) / 10;
    return { dir: d > 0 ? "up" : d < 0 ? "down" : "same", prev: p, cur: c, prevDate: prev.valueDate, curDate: cur.valueDate, isYesterday: gap === 1 };
}
/** "ระบายระดับนี้ต่อเนื่อง N วัน": how many consecutive daily reports (ending at the
 *  current report) carry the current value. Only when the run is BOUNDED by real data —
 *  a report the day before it with a different value. If the run reaches the start of
 *  the history or a missing day, its true length is unknown → null (never overclaimed).
 *  Also null for a 1-day run (that is just "changed today"). */
export function levelStreak(recent, cur) {
    if (!cur || cur.value == null)
        return null;
    const byDate = new Map();
    for (const r of recent)
        byDate.set(r.valueDate, r.value); // later entries win
    byDate.set(cur.valueDate, cur.value);
    let days = 1, d = cur.valueDate;
    for (;;) {
        const prevDay = new Date((dayNum(d) - 1) * 86400000).toISOString().slice(0, 10);
        if (!byDate.has(prevDay))
            return null; // gap / start of history → unknown
        const v = byDate.get(prevDay);
        if (v == null)
            return null;
        if (v !== cur.value)
            break; // bounded by a different real value
        days++;
        d = prevDay;
    }
    return days >= 2 ? { days, since: d } : null;
}
//# sourceMappingURL=dam-change.js.map